import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { streamChat, getUserStatus, verifyCitations, TrialExpiredError, AnonymousTrialExpiredError } from './lib/groq';
import { signIn, signOut, isSignedIn, clearTokenCache, UserStatus, UserType, getConfig, AppConfig, AnonStatus, getSimilarCases, SimilarCase, getPortalUrl, hasPremiumFeatures, canUseSimilarCases, getPricing, PricingConfig, FeatureValue, updateProfile, getAnonStatus } from './lib/auth';
import { renderTextWithCitations, getChunkPreview, getChatStorageKey } from './lib/citations';
import {
  getOrCreateEntry,
  recordActivity,
  isActive,
  getTimeStats,
  formatTime,
  formatTimeHuman,
  resetCaseTime,
  TimeEntry,
  TimeStats
} from './lib/timeTracker';
import { bridge, CaseData } from './lib/bridge-client';
import { trackEvent } from './lib/track';

// SVG Icons for header buttons
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5" />
    <path d="m5 12 7-7 7 7" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Loading indicator with rotating messages for Similar Cases
const LOADING_MESSAGES = [
  'Analyzing legal factors...',
  'Identifying key principles...',
  'Searching for precedents...',
  'Comparing case law...',
  'Finding similar decisions...',
];

function SimilarLoadingIndicator() {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(i => (i + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="similar-loading">
      <div className="similar-loading-spinner" />
      <div className="similar-loading-text">{LOADING_MESSAGES[messageIndex]}</div>
      <div className="similar-loading-subtext">This may take 30-60 seconds</div>
    </div>
  );
}

// Custom Tooltip component with smart positioning
function Tooltip({ children, text, position = 'bottom' }: { children: React.ReactNode; text: string; position?: 'top' | 'bottom' }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; actualPosition: 'top' | 'bottom' } | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const timeoutRef = useRef<number | null>(null);

  const calculatePosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    // Estimate tooltip size (will be refined after render)
    const tooltipHeight = 28;
    const tooltipWidth = Math.min(text.length * 7 + 16, 250); // Rough estimate

    // Determine vertical position
    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;
    let actualPosition: 'top' | 'bottom' = position;
    let top: number;

    if (position === 'bottom' && spaceBelow < tooltipHeight + 10) {
      actualPosition = 'top';
    } else if (position === 'top' && spaceAbove < tooltipHeight + 10) {
      actualPosition = 'bottom';
    }

    if (actualPosition === 'bottom') {
      top = rect.bottom + 6;
    } else {
      top = rect.top - tooltipHeight - 6;
    }

    // Center horizontally, but clamp to viewport
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));

    setCoords({ top, left, actualPosition });
  }, [text, position]);

  const showTooltip = useCallback(() => {
    timeoutRef.current = window.setTimeout(() => {
      calculatePosition();
      setVisible(true);
    }, 150);
  }, [calculatePosition]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
    setCoords(null);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <span
      ref={wrapperRef}
      className="tooltip-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {visible && coords && (
        <span
          ref={tooltipRef}
          className={`tooltip tooltip-fixed tooltip-${coords.actualPosition}`}
          style={{ top: coords.top, left: coords.left }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
}


// Custom component for rendering markdown with clickable citations
const MessageContent = React.memo(function MessageContent({
  content,
  onCitationClick
}: {
  content: string;
  onCitationClick: (index: number, allIndices: number[], event: React.MouseEvent) => void;
}) {
  const components = useMemo(() => ({
    p: ({ children }: { children?: React.ReactNode }) => (
      <p>{processChildren(children, onCitationClick)}</p>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li>{processChildren(children, onCitationClick)}</li>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong>{processChildren(children, onCitationClick)}</strong>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <em>{processChildren(children, onCitationClick)}</em>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td>{processChildren(children, onCitationClick)}</td>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th>{processChildren(children, onCitationClick)}</th>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="table-wrapper">
        <table>{children}</table>
      </div>
    ),
  }), [onCitationClick]);

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
});

// Process children to find and replace citation text
function processChildren(
  children: React.ReactNode,
  onCitationClick: (index: number, allIndices: number[], event: React.MouseEvent) => void
): React.ReactNode {
  return React.Children.map(children, child => {
    if (typeof child === 'string') {
      return renderTextWithCitations(child, onCitationClick);
    }
    return child;
  });
}

interface CitationPopup {
  x: number;
  y: number;
  indices: number[];
  position: 'above' | 'below';
}



export default function App() {
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [citationPopup, setCitationPopup] = useState<CitationPopup | null>(null);
  const [timeEntry, setTimeEntry] = useState<TimeEntry | null>(null);
  const [timeStats, setTimeStats] = useState<TimeStats | null>(null);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [displayTime, setDisplayTime] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [similarCases, setSimilarCases] = useState<SimilarCase[] | null>(null);
  const [similarMarkdown, setSimilarMarkdown] = useState<string | null>(null);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [modalSize, setModalSize] = useState(() => ({
    width: Math.min(600, Math.max(300, window.innerWidth * 0.95)),
    height: Math.min(600, Math.max(350, window.innerHeight * 0.85))
  }));
  const similarModalRef = useRef<HTMLDivElement>(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [pricingTab, setPricingTab] = useState<'monthly' | 'annual' | 'student'>('monthly');
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const awaitingPaymentTimeoutRef = useRef<number | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [introName, setIntroName] = useState('');
  const [introUserType, setIntroUserType] = useState<UserType | ''>('');
  const [introSubmitting, setIntroSubmitting] = useState(false);
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null);
  const [anonStatus, setAnonStatus] = useState<AnonStatus | null>(null);
  const [anonTrialExpired, setAnonTrialExpired] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamingRef = useRef('');
  const timeEntryRef = useRef<TimeEntry | null>(null);
  const isResettingRef = useRef(false);
  const isMouseDownRef = useRef(false);
  const selectedTextRef = useRef<string>('');
  const originalTabIdRef = useRef<number | null>(null);
  const timeStatsRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<HTMLDivElement>(null);
  // Flag: set when worker rejects Chrome's ambient token (401).
  // Prevents re-checking auth until user explicitly clicks "Sign in".
  const authRejectedRef = useRef(false);

  // Detect if viewing a Google Scholar case (use USD pricing)
  const isGoogleScholar = useMemo(() => {
    return caseData?.metadata.url?.includes('scholar.google.') ?? false;
  }, [caseData?.metadata.url]);

  // Currency symbol based on platform
  const currencySymbol = isGoogleScholar ? '$' : 'C$';

  // Load case data, auth status, and config on mount
  useEffect(() => {
    loadCaseData();
    checkAuth();
    getConfig().then(config => {
      if (config) setAppConfig(config);
    });
    getPricing().then(pricing => {
      if (pricing) setPricingConfig(pricing);
    });
    // Track unique panel open (fire and forget)
    try {
      let clientId = localStorage.getItem('pp_cid');
      if (!clientId) {
        clientId = crypto.randomUUID();
        localStorage.setItem('pp_cid', clientId);
      }
      navigator.sendBeacon(
        `${import.meta.env.DEV ? 'https://canlii-pinpoint-api-dev.thomoal.workers.dev' : 'https://canlii-pinpoint-api.thomoal.workers.dev'}/t/open`,
        JSON.stringify({ id: clientId })
      );
    } catch {}
  }, []);

  // Fetch anonymous status when not authenticated
  useEffect(() => {
    if (isAuthenticated === false) {
      getAnonStatus().then(status => {
        if (status) {
          setAnonStatus(status);
          if (status.remaining <= 0) {
            setAnonTrialExpired(true);
          }
        }
      });
    } else if (isAuthenticated === true) {
      // Clear anonymous state when signed in
      setAnonStatus(null);
      setAnonTrialExpired(false);
    }
  }, [isAuthenticated]);

  // Dismiss hint toast (workaround until extension update fixes close button on Mac)
  useEffect(() => {
    bridge.storageSet({ canlii_pinpoint_help_dismissed: true });
  }, []);

  // Update modal size when panel is resized
  useEffect(() => {
    const handleResize = () => {
      setModalSize(prev => ({
        width: Math.min(prev.width, Math.max(300, window.innerWidth * 0.95)),
        height: Math.min(prev.height, Math.max(350, window.innerHeight * 0.85))
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for status refresh messages via bridge
  useEffect(() => {
    const handleRefreshStatus = () => {
      if (isAuthenticated && !authRejectedRef.current) {
        getUserStatus().then(status => {
          if (status) {
            setUserStatus(status);
            if (!status.subscribed && status.trialRemaining === 0) {
              setTrialExpired(true);
            }
          }
        });
      }
    };

    bridge.on('REFRESH_STATUS', handleRefreshStatus);
    return () => bridge.off('REFRESH_STATUS', handleRefreshStatus);
  }, [isAuthenticated]);

  // When signed out, poll for sign-in from another panel (chrome.identity is extension-wide)
  useEffect(() => {
    if (isAuthenticated !== false) return;
    // Don't poll if the worker already rejected Chrome's ambient token —
    // isSignedIn() will keep returning true (Chrome always provides it),
    // causing an infinite auth flip-flop.
    if (authRejectedRef.current) return;

    const interval = setInterval(async () => {
      if (authRejectedRef.current) return;
      const signedIn = await isSignedIn();
      if (signedIn) {
        const status = await getUserStatus();
        if (!status) {
          // Worker rejected the token — stop polling
          authRejectedRef.current = true;
          return;
        }
        setIsAuthenticated(true);
        setUserStatus(status);
        if (!status.subscribed && status.trialRemaining === 0) {
          setTrialExpired(true);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Auto-refresh status - faster when awaiting payment (1s), normal otherwise (5s)
  useEffect(() => {
    if (!isAuthenticated) return;

    const pollInterval = awaitingPayment ? 1000 : 5000;

    const interval = setInterval(() => {
      getUserStatus().then(status => {
        if (status) {
          const planChanged = status.plan !== userStatus?.plan;
          const subscribedChanged = status.subscribed !== userStatus?.subscribed;

          if (planChanged || subscribedChanged) {
            setUserStatus(status);
            if (status.subscribed) {
              setTrialExpired(false);
              // Stop aggressive polling once subscribed
              if (awaitingPayment) {
                setAwaitingPayment(false);
                if (awaitingPaymentTimeoutRef.current) {
                  clearTimeout(awaitingPaymentTimeoutRef.current);
                  awaitingPaymentTimeoutRef.current = null;
                }
              }
            } else if (status.trialRemaining === 0) {
              setTrialExpired(true);
            }
          }
        } else {
          // Token revoked or rejected — reset auth state.
          // Mark as rejected so polling doesn't re-trigger the cycle.
          authRejectedRef.current = true;
          clearTokenCache();
          setIsAuthenticated(false);
          setUserStatus(null);
        }
      });
    }, pollInterval);

    return () => clearInterval(interval);
  }, [isAuthenticated, userStatus?.plan, userStatus?.subscribed, awaitingPayment]);

  // Listen for tab URL changes via bridge events
  useEffect(() => {
    const handleTabUpdate = (data: unknown) => {
      const { tabId, url } = data as { tabId: number; url: string };
      // Only care about URL changes in the ORIGINAL tab
      if (url && tabId === originalTabIdRef.current) {
        setCaseData(null);
        setMessages([]);
        setTimeEntry(null);
        setDisplayTime(0);
        setError(null);
        setTimeout(() => loadCaseData(), 500);
      }
    };

    bridge.on('TAB_UPDATED', handleTabUpdate);
    return () => bridge.off('TAB_UPDATED', handleTabUpdate);
  }, []);

  // Close time stats dropdown when clicking outside
  useEffect(() => {
    if (!showStats) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (timeStatsRef.current?.contains(target) || timerRef.current?.contains(target)) {
        return;
      }
      setShowStats(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStats]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input after loading completes
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Load chat history when case data is available
  useEffect(() => {
    if (caseData?.metadata.url) {
      loadChatHistory(caseData.metadata.url);
    }
  }, [caseData?.metadata.url]);

  // Save chat history when messages change (via bridge)
  useEffect(() => {
    if (caseData?.metadata.url && messages.length > 0) {
      const key = getChatStorageKey(caseData.metadata.url);
      bridge.storageSet({ [key]: messages });
    }
  }, [messages, caseData?.metadata.url]);

  // Initialize time tracking when case data is available
  useEffect(() => {
    if (caseData?.metadata) {
      initTimeTracking();
    }
  }, [caseData?.metadata]);

  // Keep ref in sync with state
  useEffect(() => {
    timeEntryRef.current = timeEntry;
  }, [timeEntry]);

  // Update timer display every second
  // TODO: Optimization opportunity — this 1Hz setInterval causes the entire App component to
  // re-render every second. Extracting TimerDisplay into a React.memo'd child component would
  // isolate these re-renders, but displayTime is also used in the stats dropdown (formatTimeHuman,
  // copyTimeEntry) and reset by handleResetTimer/handleTabUpdate/initTimeTracking, making extraction
  // non-trivial without a ref-based or context-based approach.
  useEffect(() => {
    const interval = setInterval(() => {
      const entry = timeEntryRef.current;
      if (!entry) return;

      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      if (isTimerActive) {
        setDisplayTime(entry.duration + (Date.now() - entry.lastActivity));
      } else {
        setDisplayTime(entry.duration);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimerActive]);

  // Listen for page activity via bridge storage changes
  useEffect(() => {
    if (!caseData?.metadata.url) return;

    const handleStorageChange = (data: unknown) => {
      const changes = data as { lastPageActivity?: { newValue?: { url: string; timestamp: number } } };
      if (changes.lastPageActivity?.newValue) {
        const activity = changes.lastPageActivity.newValue;
        if (activity.url === caseData.metadata.url) {
          handleActivity();
        }
      }
    };

    bridge.on('storage.local.onChanged', handleStorageChange);
    return () => bridge.off('storage.local.onChanged', handleStorageChange);
  }, [caseData?.metadata.url]);

  // Track activity in side panel
  useEffect(() => {
    let lastActivityTime = 0;
    const THROTTLE_MS = 2000;

    const handleMouseDown = () => {
      isMouseDownRef.current = true;
    };

    const handleMouseUp = () => {
      isMouseDownRef.current = false;
    };

    const handleSidePanelActivity = () => {
      if (isMouseDownRef.current) {
        return;
      }
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      const now = Date.now();
      if (now - lastActivityTime < THROTTLE_MS) {
        return;
      }
      lastActivityTime = now;
      handleActivity();
    };

    document.addEventListener('mousedown', handleMouseDown, { passive: true });
    document.addEventListener('mouseup', handleMouseUp, { passive: true });
    document.addEventListener('mousemove', handleSidePanelActivity, { passive: true });
    document.addEventListener('click', handleSidePanelActivity, { passive: true });
    document.addEventListener('keydown', handleSidePanelActivity, { passive: true });
    document.addEventListener('scroll', handleSidePanelActivity, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleSidePanelActivity);
      document.removeEventListener('click', handleSidePanelActivity);
      document.removeEventListener('keydown', handleSidePanelActivity);
      document.removeEventListener('scroll', handleSidePanelActivity);
    };
  }, [caseData?.metadata.url]);

  // Check for inactivity periodically
  useEffect(() => {
    if (!timeEntry) return;

    const checkInterval = setInterval(() => {
      const active = isActive(timeEntry);
      setIsTimerActive(active);
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [timeEntry]);

  async function loadChatHistory(url: string) {
    const key = getChatStorageKey(url);
    const result = await bridge.storageGet<{ [key: string]: Message[] }>([key]);
    if (Array.isArray(result[key])) {
      setMessages(result[key]);
    }
  }

  async function initTimeTracking() {
    if (!caseData?.metadata) return;

    const entry = await getOrCreateEntry(
      caseData.metadata.url,
      caseData.metadata.title,
      caseData.metadata.citation
    );
    setTimeEntry(entry);
    setDisplayTime(entry.duration);

    const stats = await getTimeStats();
    setTimeStats(stats);
  }

  async function handleActivity() {
    if (!caseData?.metadata.url) return;
    if (isResettingRef.current) return;

    const updatedEntry = await recordActivity(caseData.metadata.url);
    if (updatedEntry) {
      timeEntryRef.current = updatedEntry;
      setTimeEntry(updatedEntry);
      setIsTimerActive(true);
    }

    const stats = await getTimeStats();
    setTimeStats(stats);
  }

  async function handleResetTimer() {
    if (!caseData?.metadata.url) return;

    isResettingRef.current = true;

    const resetEntry = await resetCaseTime(caseData.metadata.url);
    if (resetEntry) {
      timeEntryRef.current = resetEntry;
      setTimeEntry(resetEntry);
      setDisplayTime(0);
    }
    const stats = await getTimeStats();
    setTimeStats(stats);

    setTimeout(() => {
      isResettingRef.current = false;
    }, 500);
  }

  function copyTimeEntry(duration: number, label: string) {
    if (!timeEntry) return;
    const timeStr = formatTimeHuman(duration);
    const text = `${timeStr} - ${label}: ${timeEntry.caseTitle}, ${timeEntry.caseCitation}`;
    navigator.clipboard.writeText(text);
  }

  function copyAggregateStat(duration: number, period: string) {
    const timeStr = formatTimeHuman(duration);
    const text = `${timeStr} - CanLII research (${period})`;
    navigator.clipboard.writeText(text);
  }

  async function clearChatHistory() {
    if (!caseData?.metadata.url) return;
    const key = getChatStorageKey(caseData.metadata.url);
    await bridge.storageRemove([key]);
    setMessages([]);
  }

  async function checkAuth() {
    // If the worker already rejected Chrome's ambient token, stay anonymous
    // until the user explicitly clicks "Sign in".
    if (authRejectedRef.current) {
      setIsAuthenticated(false);
      return;
    }

    const signedIn = await isSignedIn();
    setIsAuthenticated(signedIn);

    if (signedIn) {
      const status = await getUserStatus();
      if (!status) {
        // Token was valid for Chrome but rejected by worker (401) — fall back to anonymous.
        // Set authRejected so polling doesn't re-trigger this cycle.
        authRejectedRef.current = true;
        setIsAuthenticated(false);
        return;
      }
      setUserStatus(status);
      if (!status.subscribed && status.trialRemaining === 0) {
        setTrialExpired(true);
      }
      if (!status.onboardingComplete) {
        setIntroName(status.name || '');
        setShowIntroModal(true);
      }
    }
  }

  async function handleSignIn() {
    try {
      // Reset the rejection flag — user is explicitly requesting sign-in
      authRejectedRef.current = false;
      const userInfo = await signIn();
      if (userInfo) {
        setIsAuthenticated(true);
        setAnonStatus(null);
        setAnonTrialExpired(false);
        const status = await getUserStatus();
        setUserStatus(status);
        // Show intro modal for new users who haven't completed onboarding
        if (status && !status.onboardingComplete) {
          setIntroName(status.name || '');
          setShowIntroModal(true);
        }
      }
    } catch (err) {
      console.error('Sign in failed:', err);
      setError('Sign in failed. Please try again.');
    }
  }

  async function handleSignOut() {
    try {
      await signOut();
      setIsAuthenticated(false);
      setUserStatus(null);
    } catch (err) {
      console.error('Sign out failed:', err);
      setError('Sign out failed. Please try again.');
    }
  }

  async function handleIntroSubmit() {
    if (!introName.trim() || !introUserType) return;
    setIntroSubmitting(true);
    try {
      const result = await updateProfile(introName.trim(), introUserType);
      if (result.success) {
        setShowIntroModal(false);
        // Refresh user status to get updated data
        const status = await getUserStatus();
        setUserStatus(status);
      }
    } catch (err) {
      console.error('Profile setup failed:', err);
      setError('Profile setup failed. Please try again.');
    } finally {
      setIntroSubmitting(false);
    }
  }

  async function handleSaveName() {
    if (!editNameValue.trim() || !userStatus?.userType) return;
    setSavingName(true);
    try {
      const result = await updateProfile(editNameValue.trim(), userStatus.userType);
      if (result.success) {
        setEditingName(false);
        const status = await getUserStatus();
        setUserStatus(status);
      }
    } catch (err) {
      console.error('Save name failed:', err);
      setError('Failed to save name. Please try again.');
    } finally {
      setSavingName(false);
    }
  }

  function handleStartEditName() {
    setEditNameValue(userStatus?.name || '');
    setEditingName(true);
  }

  async function handleManageSubscription() {
    const url = await getPortalUrl();
    if (url) {
      bridge.openUrl(url);
    }
  }

  async function handleFindSimilar() {
    if (loadingSimilar) return;

    // Check if user can use case discovery (skip for anonymous — backend handles limits)
    if (isAuthenticated && !canUseSimilarCases(userStatus)) {
      setError(`You've used all ${userStatus?.similarCasesLimit} case discoveries this month.`);
      return;
    }

    setLoadingSimilar(true);
    setShowSimilarModal(true);

    try {
      // Fetch fresh case data via bridge
      const response = await bridge.getCaseData();
      if (!response?.success || !response.data) {
        return;
      }

      const freshData = response.data;

      // Build context from ALL chunks
      const context = freshData.chunks
        .map(c => c.text)
        .join('\n\n');

      // Determine source from URL
      const caseUrl = freshData.metadata.url;
      const source = caseUrl.includes('scholar.google.') ? 'scholar' : 'canlii';

      const result = await getSimilarCases(
        freshData.metadata.title,
        freshData.metadata.citation,
        caseUrl,
        context,
        source
      );

      if (result) {
        if (result.success) {
          setSimilarCases(result.data.cases);
          setSimilarMarkdown(result.data.markdown || null);
          // Refresh status to update remaining count
          if (isAuthenticated) {
            const status = await getUserStatus();
            setUserStatus(status);
          }
        } else {
          // Handle error responses (403, 429)
          setError(result.error.message);
          setShowSimilarModal(false);
        }
      }
    } catch (err) {
      console.error('Find similar cases failed:', err);
      setError('Failed to find similar cases. Please try again.');
    } finally {
      setLoadingSimilar(false);
    }
  }

  // Resize handler for similar cases modal
  const handleModalResize = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = modalSize.width;
    const startHeight = modalSize.height;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      const maxWidth = window.innerWidth * 0.95;
      const maxHeight = window.innerHeight * 0.85;

      if (direction.includes('e')) newWidth = Math.min(maxWidth, Math.max(300, startWidth + deltaX));
      if (direction.includes('w')) newWidth = Math.min(maxWidth, Math.max(300, startWidth - deltaX));
      if (direction.includes('s')) newHeight = Math.min(maxHeight, Math.max(200, startHeight + deltaY));
      if (direction.includes('n')) newHeight = Math.min(maxHeight, Math.max(200, startHeight - deltaY));

      setModalSize({ width: newWidth, height: newHeight });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [modalSize]);

  async function loadCaseData(retries = 3) {
    try {
      // Get initial context for tab info
      const context = bridge.getInitialContext();
      if (context?.tabId) {
        originalTabIdRef.current = context.tabId;
      }

      const response = await bridge.getCaseData();

      if (response?.success && response.data) {
        setCaseData(response.data);
      }
    } catch {
      if (retries > 0) {
        setTimeout(() => loadCaseData(retries - 1), 500);
      }
    }
  }

  async function jumpToChunk(index: number) {
    const chunk = caseData?.chunks.find(c => c.index === index);
    if (!chunk) return;
    await bridge.jumpToCitation(index, chunk.textStart);
  }

  const handleCitationClick = useCallback((index: number, allIndices: number[], event: React.MouseEvent) => {
    trackEvent('citationClick');
    if (allIndices.length === 1) {
      jumpToChunk(index);
      return;
    }

    event.stopPropagation();

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;

    const estimatedPopupHeight = Math.min(allIndices.length * 45 + 35, 240);
    const spaceBelow = viewportHeight - rect.bottom - 20;
    const spaceAbove = rect.top - 20;

    const showAbove = spaceBelow < estimatedPopupHeight || spaceAbove > spaceBelow * 1.5;

    const popupWidth = 320;
    const spaceRight = viewportWidth - rect.left - 10;
    const xPos = spaceRight < popupWidth ? Math.max(10, viewportWidth - popupWidth - 10) : rect.left;

    setCitationPopup({
      x: xPos,
      y: showAbove ? rect.top : rect.bottom + 4,
      indices: allIndices,
      position: showAbove ? 'above' : 'below'
    });
  }, [caseData?.chunks]);

  function handlePopupItemClick(index: number) {
    jumpToChunk(index);
    setCitationPopup(null);
  }

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      if (citationPopup) {
        const target = event.target as HTMLElement;
        if (!target.closest('.citation-popup')) {
          setCitationPopup(null);
        }
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [citationPopup]);

  // Selection popup for follow-up questions
  useEffect(() => {
    const popup = document.createElement('button');
    popup.className = 'selection-popup';
    popup.textContent = 'Follow up ↗';
    popup.style.cssText = 'position: fixed; transform: translateY(-100%); display: none;';
    document.body.appendChild(popup);

    function showPopup(x: number, y: number) {
      popup.style.left = `${x}px`;
      popup.style.top = `${y}px`;
      popup.style.display = 'block';
    }

    function hidePopup() {
      popup.style.display = 'none';
      selectedTextRef.current = '';
    }

    function handleSelectionChange() {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.toString().trim().length < 5) {
        hidePopup();
        return;
      }

      const selectedText = selection.toString().trim();

      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const messageElement = (container.nodeType === Node.TEXT_NODE ? container.parentElement : container as HTMLElement)?.closest('.message.assistant');

      if (!messageElement) {
        hidePopup();
        return;
      }

      selectedTextRef.current = selectedText.slice(0, 500);

      const rect = range.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;

      const popupWidth = 100;
      let x = rect.left + rect.width / 2 - popupWidth / 2;
      x = Math.max(10, Math.min(x, viewportWidth - popupWidth - 10));

      showPopup(x, rect.top - 8);
    }

    function handleMouseUp() {
      setTimeout(handleSelectionChange, 10);
    }

    function handleSelectionCollapse() {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        hidePopup();
      }
    }

    function handlePopupClick(e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      if (!selectedTextRef.current) return;

      const query = `Give more info about this with citations: "${selectedTextRef.current}"`;
      hidePopup();

      window.dispatchEvent(new CustomEvent('pinpoint-followup', { detail: query }));
    }

    popup.addEventListener('mousedown', (e) => e.preventDefault());
    popup.addEventListener('click', handlePopupClick);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionCollapse);

    return () => {
      popup.removeEventListener('click', handlePopupClick);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionCollapse);
      popup.remove();
    };
  }, []);

  // Listen for follow-up events
  useEffect(() => {
    function handleFollowUp(e: Event) {
      const query = (e as CustomEvent).detail;
      if (query) {
        handleSubmit(undefined, query);
      }
    }
    window.addEventListener('pinpoint-followup', handleFollowUp);
    return () => window.removeEventListener('pinpoint-followup', handleFollowUp);
  }, [caseData, isAuthenticated, isLoading, messages, anonTrialExpired]);

  async function handleSubmit(e?: React.FormEvent, directMessage?: string) {
    e?.preventDefault();
    const messageText = directMessage || input.trim();
    if (!messageText || isLoading || !caseData) return;

    // Block if anonymous trial expired
    if (!isAuthenticated && anonTrialExpired) {
      setAnonTrialExpired(true);
      return;
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageText
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    setStreamingContent('');
    streamingRef.current = '';

    try {
      const history = messages
        .filter(m => m.role !== 'system')
        .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      const stream = streamChat(
        caseData.metadata,
        caseData.chunks,
        userMessage.content,
        history,
        isGoogleScholar ? 'scholar' : 'canlii'
      );

      let lastUpdate = 0;
      const UPDATE_INTERVAL = 50;

      for await (const chunk of stream) {
        streamingRef.current += chunk;
        const now = Date.now();
        if (now - lastUpdate > UPDATE_INTERVAL) {
          setStreamingContent(streamingRef.current);
          lastUpdate = now;
        }
      }

      const { correctedResponse } = verifyCitations(streamingRef.current, caseData.chunks);

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: correctedResponse
      };
      setMessages(prev => [...prev, assistantMessage]);
      setStreamingContent('');

      // Refresh status
      if (isAuthenticated) {
        const status = await getUserStatus();
        setUserStatus(status);
      } else {
        const status = await getAnonStatus();
        if (status) {
          setAnonStatus(status);
          if (status.remaining <= 0) {
            setAnonTrialExpired(true);
          }
        }
      }
    } catch (err) {
      if (err instanceof TrialExpiredError) {
        setTrialExpired(true);
        setError(err.message);
      } else if (err instanceof AnonymousTrialExpiredError) {
        setAnonTrialExpired(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to get response');
      }
    } finally {
      setIsLoading(false);
      streamingRef.current = '';
    }
  }

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="app">
        <div className="header">
          <h1>Pinpoint</h1>
        </div>
        <div className="setup-required">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show trial expired screen (authenticated users only)
  if (isAuthenticated && trialExpired) {
    return (
      <div className="app">
        <div className="header">
          <h1>Pinpoint</h1>
        </div>
        {awaitingPayment && (
          <div className="payment-pending-banner">
            <div className="payment-pending-spinner" />
            <span>Your subscription is being activated. It may take up to a minute for changes to appear.</span>
          </div>
        )}
        <div className="setup-required">
          <h2>{awaitingPayment ? 'Activating...' : 'Trial Ended'}</h2>
          {!awaitingPayment && (
            <>
              <p>You've used all {userStatus?.trialLimit ?? appConfig?.anonymousTrialLimit ?? 10} free messages.</p>
              <p className="setup-instructions">
                Subscribe to continue using Pinpoint for legal research.
              </p>
              <button
                className="setup-button"
                onClick={() => setShowSubscribeModal(true)}
              >
                View Plans
              </button>
            </>
          )}
          {awaitingPayment && (
            <p>Hang tight while we confirm your payment with Stripe.</p>
          )}
          <button className="link-button" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
        {showSubscribeModal && pricingConfig && renderSubscribeModal()}
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="app">
        <div className="header">
          <h1>Pinpoint</h1>
        </div>
        <div className="no-case">
          <h2>No Case Loaded</h2>
          <p>Navigate to a CanLII case page to use this tool.</p>
        </div>
      </div>
    );
  }

  // Build Stripe URL with prefilled email if available
  function getStripeUrl(baseUrl: string): string {
    if (userStatus?.email) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}prefilled_email=${encodeURIComponent(userStatus.email)}`;
    }
    return baseUrl;
  }

  // Open payment URL and start aggressive polling for subscription updates
  function openPaymentUrl(url: string) {
    bridge.openUrl(getStripeUrl(url));
    setShowSubscribeModal(false);
    setAwaitingPayment(true);

    // Stop aggressive polling after 5 minutes if no subscription detected
    if (awaitingPaymentTimeoutRef.current) {
      clearTimeout(awaitingPaymentTimeoutRef.current);
    }
    awaitingPaymentTimeoutRef.current = window.setTimeout(() => {
      setAwaitingPayment(false);
      awaitingPaymentTimeoutRef.current = null;
    }, 300000);
  }

  function renderStudentPricingTable() {
    if (!pricingConfig) return null;

    const studentTiers = pricingConfig.studentTiers;
    const featuredTierId = 'scholar';
    const caseDiscoveryFeature = pricingConfig.studentFeatures.find(f => f.name === 'Case Discovery');

    return (
      <>
        <div className="pricing-cards">
          {studentTiers.map(tier => {
            const isFeatured = tier.id === featuredTierId;
            const link = isGoogleScholar ? tier.stripeLinkUsd : tier.stripeLink;
            const caseDiscoveryVal = caseDiscoveryFeature
              ? (caseDiscoveryFeature[tier.id as keyof typeof caseDiscoveryFeature] as FeatureValue)
              : 0;

            const features: { content: React.ReactNode; included: boolean }[] = [
              {
                content: <><strong>{tier.messages}</strong> messages per month</>,
                included: true,
              },
              {
                content: caseDiscoveryVal === 'Unlimited'
                  ? <><strong>Unlimited</strong> case discoveries</>
                  : <><strong>{caseDiscoveryVal}</strong> case discoveries per month</>,
                included: typeof caseDiscoveryVal === 'number' ? caseDiscoveryVal > 0 : true,
              },
              { content: 'Gist summaries', included: true },
              ...(isGoogleScholar ? [{ content: 'Automatic Bluebook citations', included: tier.id === 'scholar' }] : []),
              { content: 'Time tracking', included: false },
            ];

            return (
              <div key={tier.id} className={`pricing-card ${isFeatured ? 'featured' : ''}`}>
                {isFeatured && <div className="pricing-card-badge">Best value</div>}
                <div className="pricing-card-name">{tier.name}</div>
                <div className="pricing-card-price">
                  <span className="pricing-card-amount">{currencySymbol}{tier.monthlyPrice}</span>
                  <span className="pricing-card-period">/mo</span>
                </div>
                <div className="pricing-card-trial">First month free</div>
                <ul className="pricing-card-features">
                  {features.map((f, i) => (
                    <li key={i} className={f.included ? '' : 'disabled'}>
                      <span className={`pricing-card-icon ${f.included ? 'check' : 'x'}`}>
                        {f.included ? '✓' : '✕'}
                      </span>
                      <span>{f.content}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`pricing-card-cta ${isFeatured ? 'featured' : ''}`}
                  onClick={() => openPaymentUrl(link)}
                >
                  Get Started
                </button>
              </div>
            );
          })}
        </div>
        <p className="pricing-student-note">Monthly billing only. Verify your student status after installing.</p>
      </>
    );
  }

  function renderRegularPricingTable() {
    if (!pricingConfig) return null;

    const tiers = pricingTab === 'annual'
      ? pricingConfig.tiers.filter(t => t.annualMonthlyPrice !== null)
      : pricingConfig.tiers;
    const featuredTierId = 'professional';
    const caseDiscoveryFeature = pricingConfig.features.find(f => f.name === 'Case Discovery');

    return (
      <>
        <div className="pricing-cards">
          {tiers.map(tier => {
            const monthlyPrice = isGoogleScholar ? tier.monthlyPriceUsd : tier.monthlyPrice;
            const annualMonthlyPrice = isGoogleScholar ? tier.annualMonthlyPriceUsd : tier.annualMonthlyPrice;
            const price = pricingTab === 'monthly' ? monthlyPrice : annualMonthlyPrice;
            const isFeatured = tier.id === featuredTierId;
            const link = pricingTab === 'monthly'
              ? (isGoogleScholar ? tier.stripeMonthlyLinkUsd : tier.stripeMonthlyLink)
              : (isGoogleScholar ? tier.stripeAnnualLinkUsd : tier.stripeAnnualLink);
            const caseDiscoveryVal = caseDiscoveryFeature
              ? (caseDiscoveryFeature[tier.id as keyof typeof caseDiscoveryFeature] as FeatureValue)
              : 0;

            const features: { content: React.ReactNode; included: boolean }[] = [
              {
                content: tier.messages === 'Unlimited'
                  ? <><strong>Unlimited</strong> messages</>
                  : <><strong>{tier.messages}</strong> messages per month</>,
                included: true,
              },
              {
                content: caseDiscoveryVal === 'Unlimited'
                  ? <><strong>Unlimited</strong> case discoveries</>
                  : <><strong>{caseDiscoveryVal}</strong> case discoveries per month</>,
                included: typeof caseDiscoveryVal === 'number' ? caseDiscoveryVal > 0 : true,
              },
              { content: 'Gist summaries', included: true },
              ...(isGoogleScholar ? [{ content: 'Automatic Bluebook citations', included: tier.id === 'professional' }] : []),
              { content: 'Time tracking', included: tier.id === 'professional' },
            ];

            return (
              <div key={tier.id} className={`pricing-card ${isFeatured ? 'featured' : ''}`}>
                {isFeatured && <div className="pricing-card-badge">Best value</div>}
                <div className="pricing-card-name">{tier.name}</div>
                <div className="pricing-card-price">
                  <span className="pricing-card-amount">{currencySymbol}{price}</span>
                  <span className="pricing-card-period">/mo</span>
                </div>
                {pricingTab === 'annual' && <div className="pricing-card-annual">billed annually</div>}
                <div className="pricing-card-trial">First month free</div>
                <ul className="pricing-card-features">
                  {features.map((f, i) => (
                    <li key={i} className={f.included ? '' : 'disabled'}>
                      <span className={`pricing-card-icon ${f.included ? 'check' : 'x'}`}>
                        {f.included ? '✓' : '✕'}
                      </span>
                      <span>{f.content}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`pricing-card-cta ${isFeatured ? 'featured' : ''}`}
                  onClick={() => openPaymentUrl(link!)}
                >
                  Get Started
                </button>
              </div>
            );
          })}
        </div>
        <div className="enterprise-card">
          <div className="enterprise-info">
            <span className="enterprise-name">For teams & firms</span>
            <span className="enterprise-desc">Custom seats, billing, and onboarding for your organization</span>
          </div>
          <button className="enterprise-cta" onClick={() => { bridge.openUrl(`mailto:${pricingConfig.enterpriseEmail}?subject=Enterprise%20Inquiry`); }}>
            Contact Us
          </button>
        </div>
      </>
    );
  }

  function renderSubscribeModal() {
    if (!pricingConfig) return null;

    return (
      <div className="similar-modal-overlay" onClick={() => setShowSubscribeModal(false)}>
        <div className="subscribe-modal" onClick={e => e.stopPropagation()}>
          <div className="similar-modal-header">
            <h3>{userStatus?.subscribed ? 'Change Plan' : 'Subscribe to Pinpoint'}</h3>
            <button className="similar-modal-close" onClick={() => setShowSubscribeModal(false)}>×</button>
          </div>
          <div className="subscribe-modal-content">
            <div className="free-trial-banner">
              <span className="free-trial-banner-icon">✦</span>
              <span>All plans include <strong>1 month free</strong> — cancel anytime</span>
            </div>
            <div className="pricing-toggle">
              <button
                className={`pricing-toggle-btn ${pricingTab === 'monthly' ? 'active' : ''}`}
                onClick={() => setPricingTab('monthly')}
              >
                Monthly
              </button>
              <button
                className={`pricing-toggle-btn ${pricingTab === 'annual' ? 'active' : ''}`}
                onClick={() => setPricingTab('annual')}
              >
                Annual
                <span className="pricing-save-badge">2 months free</span>
              </button>
              <button
                className={`pricing-toggle-btn ${pricingTab === 'student' ? 'active' : ''}`}
                onClick={() => setPricingTab('student')}
              >
                🎓 Student
              </button>
            </div>
            {pricingTab === 'student' ? renderStudentPricingTable() : renderRegularPricingTable()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <div className="header-row">
          <Tooltip text={caseData.metadata.title}>
            <div className="case-info">
              <span className="case-title">{caseData.metadata.title}</span>
              <span className="case-citation">{caseData.metadata.citation}</span>
            </div>
          </Tooltip>
          <div className="header-actions">
            {((!isAuthenticated && !anonTrialExpired) || (userStatus && userStatus.similarCasesLimit > 0)) && (
              <Tooltip text={!isAuthenticated ? 'Case Discovery' : (userStatus && userStatus.similarCasesRemaining > 0 ? `Case Discovery (${userStatus.similarCasesRemaining} remaining)` : 'No case discoveries remaining')}>
                <button
                  className={`header-btn similar-btn ${isAuthenticated && userStatus && userStatus.similarCasesRemaining === 0 ? 'disabled' : ''}`}
                  onClick={handleFindSimilar}
                  disabled={isAuthenticated ? (userStatus ? userStatus.similarCasesRemaining === 0 : true) : false}
                >
                  <SearchIcon />
                </button>
              </Tooltip>
            )}
            {(hasPremiumFeatures(userStatus) || !isAuthenticated) && timeEntry && (
              <Tooltip text="Click for stats">
                <div
                  ref={timerRef}
                  className={`timer-compact ${isTimerActive ? 'active' : 'paused'}`}
                  onClick={() => { if (!showStats) trackEvent('timePanel'); setShowStats(!showStats); }}
                >
                  <span className="timer-value">{formatTime(displayTime)}</span>
                  {!isTimerActive && <span className="timer-status">⏸</span>}
                </div>
              </Tooltip>
            )}
            {messages.length > 0 && (
              <Tooltip text="Reset chat">
                <button className="header-btn" onClick={clearChatHistory}>
                  <RefreshIcon />
                </button>
              </Tooltip>
            )}
            <Tooltip text="Help">
              <button className="header-btn header-btn-icon" onClick={() => setShowHelpModal(true)}>
                ?
              </button>
            </Tooltip>
          </div>
        </div>
        {(hasPremiumFeatures(userStatus) || !isAuthenticated) && showStats && timeStats && (
          <div className="time-stats" ref={timeStatsRef}>
            <button className="time-stats-close" onClick={() => setShowStats(false)}>&times;</button>
            <div className="stat-section-label">This case</div>
            <div className="stat-row">
              <span>Total</span>
              <span className="stat-value">
                {formatTimeHuman(displayTime)}
                <Tooltip text="Copy time entry" position="top">
                  <button className="stat-copy-inline" onClick={() => copyTimeEntry(displayTime, 'Research')}>📋</button>
                </Tooltip>
              </span>
            </div>
            <div className="stat-section-label">All CanLII research</div>
            <div className="stat-row">
              <span>Today ({timeStats.todayCases} {timeStats.todayCases === 1 ? 'case' : 'cases'})</span>
              <span className="stat-value">
                {formatTimeHuman(timeStats.today)}
                <Tooltip text="Copy time entry" position="top">
                  <button className="stat-copy-inline" onClick={() => copyAggregateStat(timeStats.today, 'today')}>📋</button>
                </Tooltip>
              </span>
            </div>
            <div className="stat-row">
              <span>This week ({timeStats.thisWeekCases} {timeStats.thisWeekCases === 1 ? 'case' : 'cases'})</span>
              <span className="stat-value">
                {formatTimeHuman(timeStats.thisWeek)}
                <Tooltip text="Copy time entry" position="top">
                  <button className="stat-copy-inline" onClick={() => copyAggregateStat(timeStats.thisWeek, 'this week')}>📋</button>
                </Tooltip>
              </span>
            </div>
            <div className="stat-row">
              <span>This month ({timeStats.thisMonthCases} {timeStats.thisMonthCases === 1 ? 'case' : 'cases'})</span>
              <span className="stat-value">
                {formatTimeHuman(timeStats.thisMonth)}
                <Tooltip text="Copy time entry" position="top">
                  <button className="stat-copy-inline" onClick={() => copyAggregateStat(timeStats.thisMonth, 'this month')}>📋</button>
                </Tooltip>
              </span>
            </div>
            <div className="stat-row">
              <span>All time ({timeStats.caseCount} {timeStats.caseCount === 1 ? 'case' : 'cases'})</span>
              <span className="stat-value">
                {formatTimeHuman(timeStats.allTime)}
                <Tooltip text="Copy time entry" position="top">
                  <button className="stat-copy-inline" onClick={() => copyAggregateStat(timeStats.allTime, 'all time')}>📋</button>
                </Tooltip>
              </span>
            </div>
            <button className="stat-reset-btn" onClick={handleResetTimer}>
              <RefreshIcon /> Reset timer for this case
            </button>
          </div>
        )}
      </div>

      {!isAuthenticated && anonStatus && (
        <div className="trial-bar anon-trial-bar" onClick={handleSignIn}>
          <div className="trial-bar-content">
            <span className="trial-bar-text">{anonStatus.remaining} of {anonStatus.limit} free messages left</span>
            <span className="trial-bar-cta">Sign in</span>
          </div>
          <div className="trial-bar-progress">
            <div
              className="trial-bar-progress-fill"
              style={{ width: `${(anonStatus.remaining / anonStatus.limit) * 100}%` }}
            />
          </div>
        </div>
      )}

      {isAuthenticated && userStatus && !userStatus.subscribed && userStatus.trialRemaining !== null && (
        <div className="trial-bar" onClick={() => setShowSubscribeModal(true)}>
          <div className="trial-bar-content">
            <span className="trial-bar-text">{userStatus.trialRemaining} of {userStatus.trialLimit} free messages left</span>
            <span className="trial-bar-cta">Upgrade</span>
          </div>
          <div className="trial-bar-progress">
            <div
              className="trial-bar-progress-fill"
              style={{ width: `${(userStatus.trialRemaining / (userStatus.trialLimit ?? 50)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {awaitingPayment && (
        <div className="payment-pending-banner">
          <div className="payment-pending-spinner" />
          <span>Your subscription is being activated. It may take up to a minute for changes to appear.</span>
        </div>
      )}

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {messages.length === 0 && !isLoading ? (
        <div className="empty-state">
          <h2>Chat with this case</h2>
          <p>Get AI-powered answers with clickable sentence-level citations.</p>
          <div className="suggestion-chips">
            <button className="suggestion-chip" onClick={() => handleSubmit(undefined, 'What are the key facts?')}>
              Key facts
            </button>
            <button className="suggestion-chip" onClick={() => handleSubmit(undefined, 'What are the legal issues?')}>
              Legal issues
            </button>
            <button className="suggestion-chip" onClick={() => handleSubmit(undefined, 'What is the holding?')}>
              Holding
            </button>
            <button className="suggestion-chip" onClick={() => handleSubmit(undefined, "What is the court's reasoning?")}>
              Reasoning
            </button>
          </div>
        </div>
      ) : (
        <div className="messages">
          {messages.map(message => (
            <div key={message.id} className={`message ${message.role}`}>
              <MessageContent
                content={message.content}
                onCitationClick={handleCitationClick}
              />
            </div>
          ))}
          {isLoading && (
            <div className="message assistant">
              {streamingContent ? (
                <MessageContent
                  content={streamingContent}
                  onCitationClick={handleCitationClick}
                />
              ) : (
                <div className="loading">
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {anonTrialExpired && !isAuthenticated && (
        <div className="anon-expired-overlay">
          <div className="anon-expired-content">
            <h3>Free messages used</h3>
            <p>You've used all {appConfig?.anonymousTrialLimit ?? 10} free messages. Subscribe to continue.</p>
            <button className="google-signin-btn" onClick={handleSignIn}>
              <GoogleIcon />
              <span>Sign in to subscribe</span>
            </button>
          </div>
        </div>
      )}

      <div className="input-area">
        <form className="input-form" onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            className="input-field"
            placeholder={anonTrialExpired && !isAuthenticated ? 'Sign in to continue...' : 'Chat with this case'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            disabled={isLoading || (anonTrialExpired && !isAuthenticated)}
            rows={1}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!input.trim() || isLoading || (anonTrialExpired && !isAuthenticated)}
          >
            <SendIcon />
          </button>
        </form>
        <div className="footer-links">
          {appConfig?.feedbackUrl && <a href={appConfig.feedbackUrl} onClick={(e) => { e.preventDefault(); bridge.openUrl(appConfig.feedbackUrl); }}>Feedback</a>}
          <span className="footer-sep">·</span>
          {isAuthenticated ? (
            <>
              <button onClick={() => setShowAccountModal(true)}>Account</button>
              <span className="footer-sep">·</span>
              <button onClick={handleSignOut}>Sign out</button>
            </>
          ) : (
            <button onClick={handleSignIn}>Sign in</button>
          )}
        </div>
      </div>

      {citationPopup && caseData && (() => {
        const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
        const availableSpace = citationPopup.position === 'above'
          ? citationPopup.y - 10
          : viewportHeight - citationPopup.y - 10;
        const constrainedMaxHeight = Math.min(240, availableSpace);

        return (
          <div
            className={`citation-popup ${citationPopup.position === 'above' ? 'citation-popup-above' : ''}`}
            style={{
              position: 'fixed',
              left: citationPopup.x,
              top: citationPopup.y,
              transform: citationPopup.position === 'above' ? 'translateY(-100%) translateY(-4px)' : undefined,
              maxHeight: constrainedMaxHeight > 80 ? constrainedMaxHeight : undefined
            }}
          >
            <div className="citation-popup-title">Jump to source:</div>
            {citationPopup.indices.map((idx) => (
              <button
                key={idx}
                className="citation-popup-item"
                onClick={() => handlePopupItemClick(idx)}
              >
                <span className="citation-popup-text">"{getChunkPreview(caseData.chunks, idx, 60)}"</span>
              </button>
            ))}
          </div>
        );
      })()}

      {showSimilarModal && (
        <div className="similar-modal-overlay" onClick={() => setShowSimilarModal(false)}>
          <div
            ref={similarModalRef}
            className="similar-modal similar-modal-large similar-modal-resizable"
            style={{ width: `${modalSize.width}px`, height: `${modalSize.height}px` }}
            onClick={e => e.stopPropagation()}
          >
            <div className="resize-handle resize-n" onMouseDown={e => handleModalResize(e, 'n')} />
            <div className="resize-handle resize-s" onMouseDown={e => handleModalResize(e, 's')} />
            <div className="resize-handle resize-e" onMouseDown={e => handleModalResize(e, 'e')} />
            <div className="resize-handle resize-w" onMouseDown={e => handleModalResize(e, 'w')} />
            <div className="resize-handle resize-ne" onMouseDown={e => handleModalResize(e, 'ne')} />
            <div className="resize-handle resize-nw" onMouseDown={e => handleModalResize(e, 'nw')} />
            <div className="resize-handle resize-se" onMouseDown={e => handleModalResize(e, 'se')} />
            <div className="resize-handle resize-sw" onMouseDown={e => handleModalResize(e, 'sw')} />

            <div className="similar-modal-header">
              <h3>Case Discovery</h3>
              <button className="similar-modal-close" onClick={() => setShowSimilarModal(false)}>×</button>
            </div>
            <div className="similar-modal-content">
              {loadingSimilar ? (
                <SimilarLoadingIndicator />
              ) : similarMarkdown ? (
                <div className="similar-markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          onClick={(e) => {
                            e.preventDefault();
                            if (href) bridge.openUrl(href);
                          }}
                        >
                          {children}
                        </a>
                      )
                    }}
                  >
                    {similarMarkdown}
                  </ReactMarkdown>
                </div>
              ) : similarCases && similarCases.length > 0 ? (
                <div className="similar-list">
                  {similarCases.map((c, i) => (
                    <a
                      key={i}
                      className="similar-item"
                      href={c.url}
                      onClick={(e) => {
                        e.preventDefault();
                        bridge.openUrl(c.url);
                      }}
                    >
                      {c.title}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="similar-empty">No similar cases found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {showSubscribeModal && renderSubscribeModal()}

      {showAccountModal && pricingConfig && (
        <div className="similar-modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="similar-modal account-modal" onClick={e => e.stopPropagation()}>
            <div className="similar-modal-header">
              <h3>Account</h3>
              <button className="similar-modal-close" onClick={() => setShowAccountModal(false)}>×</button>
            </div>
            <div className="similar-modal-content account-content">
              <div className="account-section">
                <div className="account-row">
                  <span className="account-label">Name</span>
                  {editingName ? (
                    <div className="account-edit-name">
                      <input
                        type="text"
                        value={editNameValue}
                        onChange={e => setEditNameValue(e.target.value)}
                        placeholder="Your name"
                        autoFocus
                      />
                      <button
                        className="account-edit-btn save"
                        onClick={handleSaveName}
                        disabled={!editNameValue.trim() || savingName}
                      >
                        {savingName ? '...' : 'Save'}
                      </button>
                      <button
                        className="account-edit-btn cancel"
                        onClick={() => setEditingName(false)}
                        disabled={savingName}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span className="account-value account-value-editable" onClick={handleStartEditName}>
                      {userStatus?.name || 'Add name'}
                      <span className="edit-icon">✎</span>
                    </span>
                  )}
                </div>
                <div className="account-row">
                  <span className="account-label">Email</span>
                  <span className="account-value">{userStatus?.email}</span>
                </div>
                <div className="account-row">
                  <span className="account-label">Plan</span>
                  <span className="account-value">
                    {userStatus?.subscribed
                      ? `${pricingConfig.tiers.find(t => t.id === userStatus.plan)?.name || pricingConfig.studentTiers.find(t => t.id === userStatus.plan)?.name || userStatus.plan} (${userStatus.billingInterval === 'year' ? 'Annual' : 'Monthly'})`
                      : 'Free Trial'}
                    {userStatus?.subscribed && (userStatus.stripe?.cancelAtPeriodEnd || userStatus.stripe?.cancelAt || userStatus.cancelledAt) && (
                      <span className="account-cancelled-pill">
                        {userStatus.stripe?.cancelAt
                          ? `Cancels ${new Date(userStatus.stripe.cancelAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                          : userStatus.stripe?.currentPeriodEnd
                            ? `Cancels ${new Date(userStatus.stripe.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : 'Cancelling'}
                      </span>
                    )}
                  </span>
                </div>
                <div className="account-row">
                  <span className="account-label">Messages</span>
                  <span className="account-value">
                    {userStatus?.subscribed
                      ? (userStatus.plan === 'professional'
                        ? 'Unlimited'
                        : (userStatus.paidLimit
                          ? `${userStatus.paidRemaining} of ${userStatus.paidLimit} remaining`
                          : 'Unlimited'))
                      : `${userStatus?.trialRemaining} of ${userStatus?.trialLimit} remaining`}
                    {userStatus?.subscribed && userStatus.stripe?.currentPeriodEnd && (
                      <span className="account-reset-date">
                        Resets {new Date(userStatus.stripe.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </span>
                </div>
                {userStatus && userStatus.similarCasesLimit > 0 && (
                  <div className="account-row">
                    <span className="account-label">Case Discovery</span>
                    <span className="account-value">
                      {userStatus.similarCasesRemaining} of {userStatus.similarCasesLimit} remaining
                      {userStatus.subscribed && userStatus.stripe?.currentPeriodEnd && (
                        <span className="account-reset-date">
                          Resets {new Date(userStatus.stripe.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="account-actions">
                {userStatus?.subscribed ? (
                  <button
                    className="account-btn primary"
                    onClick={() => { setShowAccountModal(false); handleManageSubscription(); }}
                  >
                    Manage Subscription
                  </button>
                ) : (
                  <button
                    className="account-btn primary"
                    onClick={() => { setShowAccountModal(false); setShowSubscribeModal(true); }}
                  >
                    Subscribe
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showHelpModal && (
        <div className="similar-modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="similar-modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="similar-modal-header">
              <h3>Tips</h3>
              <button className="similar-modal-close" onClick={() => setShowHelpModal(false)}>×</button>
            </div>
            <div className="similar-modal-content help-content">
              <div className="help-section">
                <h4>Quick Actions</h4>
                <div className="help-item">
                  <span className="help-icon"><SearchIcon /></span>
                  <div><strong>Case Discovery</strong> <span className="pro-badge">Pro</span> – Find related cases using AI search</div>
                </div>
                <div className="help-item">
                  <span className="help-icon"><RefreshIcon /></span>
                  <div><strong>Reset Chat</strong> – Reset conversation for this case</div>
                </div>
              </div>

              <div className="help-section">
                <h4>Text Selection</h4>
                <div className="help-item">
                  <div><strong>On {isGoogleScholar ? 'Google Scholar' : 'CanLII'} page:</strong> Select text → right-click → "Get Gist" for a summary</div>
                </div>
                {isGoogleScholar && (
                  <div className="help-item">
                    <div><strong>On Google Scholar page:</strong> Select text → right-click → "Get Bluebook Citation"</div>
                  </div>
                )}
                <div className="help-item">
                  <div><strong>In chat:</strong> Select text → click "Follow up" to ask about it</div>
                </div>
              </div>

              <div className="help-section">
                <h4>Time Tracking <span className="pro-badge">Pro</span></h4>
                <div className="help-item">
                  <div>Click the timer to see stats breakdown and copy time entries for billing</div>
                </div>
              </div>

              <div className="help-section">
                <h4>Pro Tips</h4>
                <div className="help-item">
                  <div>Ask <em>"give a table of all costs"</em> for cost summaries in tables</div>
                </div>
                <div className="help-item">
                  <div>Click citation arrows <span className="citation-badge-mini">↗</span> to jump to source paragraphs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showIntroModal && (
        <div className="similar-modal-overlay">
          <div className="similar-modal intro-modal" onClick={e => e.stopPropagation()}>
            <div className="similar-modal-header">
              <h3>Welcome to Pinpoint!</h3>
            </div>
            <div className="similar-modal-content intro-content">
              <div className="intro-form">
                <div className="intro-field">
                  <input
                    id="intro-name"
                    type="text"
                    value={introName}
                    onChange={e => setIntroName(e.target.value)}
                    placeholder="Your name"
                    autoFocus
                  />
                </div>

                <div className="intro-field">
                  <label>I am a...</label>
                  <div className="intro-options">
                    {([
                      { value: 'law_student', label: 'Law Student' },
                      { value: 'paralegal', label: 'Paralegal' },
                      { value: 'lawyer', label: 'Lawyer' },
                      { value: 'researcher', label: 'Researcher' },
                      { value: 'other', label: 'Other' },
                    ] as const).map(option => (
                      <button
                        key={option.value}
                        className={`intro-option ${introUserType === option.value ? 'selected' : ''}`}
                        onClick={() => setIntroUserType(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  className="intro-submit"
                  onClick={handleIntroSubmit}
                  disabled={!introName.trim() || !introUserType || introSubmitting}
                >
                  {introSubmitting ? 'Saving...' : 'Get Started'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
